import React from "react";
import type { User } from "../UsuariosPageNew";
import { getInitials } from "../UsuariosPageNew";

interface UserActionModalProps {
    isOpen: boolean;
    selectedUser: User | null;
    actionType: "status" | "superhost" | "suspend" | "reactivate";
    isUpdating: boolean;
    suspensionConfig?: any;
    suspendType: string;
    setSuspendType: (type: string) => void;
    suspendDuration: number;
    setSuspendDuration: (duration: number) => void;
    suspendReason: string;
    setSuspendReason: (reason: string) => void;
    reasonError: boolean;
    setReasonError: (err: boolean) => void;
    roleToSuspend: string;
    setRoleToSuspend: (role: string) => void;
    onClose: () => void;
    onConfirm: () => void;
}

const UserActionModal: React.FC<UserActionModalProps> = ({
    isOpen, selectedUser, actionType, isUpdating, suspensionConfig,
    suspendType, setSuspendType, suspendDuration, setSuspendDuration,
    suspendReason, setSuspendReason, reasonError, setReasonError,
    roleToSuspend, setRoleToSuspend,
    onClose, onConfirm
}) => {
    if (!isOpen || !selectedUser) return null;

    const activeBlocks = suspensionConfig?.currentBlocks?.filter((b: any) => b.isBlocked) || [];

    const isSuspending = actionType === "suspend" || (actionType === "status" && activeBlocks.length === 0);

    const showRoleSelection = suspensionConfig?.config?.roles?.length > 1;
    const activeTypeConfig = suspensionConfig?.config?.suspensionTypes?.find((t: any) => t.id === suspendType);
    const showDurationSelector = activeTypeConfig?.allowsDuration === true;

    return (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-white border border-gray-200 rounded-xl p-7 w-[420px] max-w-[95vw] shadow-xl">

                {/* Ícono superior */}
                <div
                    className="w-[52px] h-[52px] rounded-xl mx-auto mb-4 flex items-center justify-center text-[26px]"
                    style={{ background: actionType === "superhost" ? "rgba(245,158,11,0.15)" : isSuspending ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)" }}
                >
                    {actionType === "superhost" ? "⭐" : isSuspending ? "🚫" : "✅"}
                </div>

                {/* Título */}
                <h3 className="text-[17px] font-bold text-center mb-1 text-gray-900">
                    {actionType === "superhost"
                        ? selectedUser.isSuperHost ? "Quitar Super Anfitrión" : "Otorgar Super Anfitrión"
                        : isSuspending ? "Suspender cuenta" : "Reactivar cuenta"}
                </h3>

                {/* Subtítulo */}
                <p className="text-xs text-gray-500 text-center mb-4">
                    {actionType === "superhost"
                        ? selectedUser.isSuperHost ? "¿Quieres quitarle esta distinción?" : "¿Quieres otorgarle la distinción de Super Anfitrión?"
                        : isSuspending
                            ? "Selecciona el rol, tipo de suspensión y la razón."
                            : "Este usuario tiene bloqueos activos. ¿Deseas restaurar su acceso?"}
                </p>

                {/* Ficha del usuario */}
                <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-lg p-2.5 px-3.5 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 overflow-hidden bg-gradient-to-br from-[#4f6df5] to-[#2563eb]">
                        {selectedUser.photoLink ? <img src={selectedUser.photoLink} alt={selectedUser.name} className="w-full h-full object-cover" /> : getInitials(selectedUser.name, selectedUser.lastName)}
                    </div>
                    <div>
                        <p className="text-[13px] font-semibold m-0 text-gray-900">{selectedUser.name} {selectedUser.lastName}</p>
                        <span className="text-[11px] text-gray-500">{actionType === "superhost" ? "Anfitrión verificado" : (selectedUser.email || "Sin correo")}</span>
                    </div>
                </div>

                {/* ALERTA DE BLOQUEOS ACTUALES (Para cualquier acción que no sea superhost) */}
                {actionType !== "superhost" && activeBlocks.length > 0 && (
                    <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <p className="text-[11px] font-bold text-orange-800 uppercase mb-1.5 flex items-center gap-1">
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                            Bloqueos activos ({activeBlocks.length})
                        </p>
                        <ul className="m-0 pl-4 text-xs text-orange-700 list-disc">
                            {activeBlocks.map((block: any, idx: number) => (
                                <li key={idx} className="mb-1">
                                    <strong>{block.roleLabel}</strong>: {block.reason}
                                    {block.endDate && ` (Hasta: ${new Date(block.endDate).toLocaleDateString()})`}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* BLOQUE DE CONFIGURACIÓN DINÁMICA (Si no es superhost) */}
                {actionType !== "superhost" && suspensionConfig && (
                    <>
                        {/* SELECCIÓN DE ROL */}
                        {showRoleSelection && (
                            <div className="mb-4">
                                <p className="text-[11px] font-bold text-gray-500 uppercase mb-2">Aplicar acción a:</p>
                                <div className="flex gap-2">
                                    {suspensionConfig.config.roles.map((rolOpt: any) => (
                                        <label key={rolOpt.key} className={`flex-1 py-2 rounded-lg text-[10px] sm:text-xs font-semibold border text-center cursor-pointer transition-all flex items-center justify-center ${roleToSuspend === rolOpt.key ? (isSuspending ? "bg-red-50 border-red-500 text-red-600" : "bg-emerald-50 border-emerald-500 text-emerald-600") : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"}`}>
                                            <input type="radio" className="hidden" name="roleSuspend" value={rolOpt.key} checked={roleToSuspend === rolOpt.key} onChange={(e) => setRoleToSuspend(e.target.value)} />
                                            {rolOpt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* FORMULARIO DE RAZÓN Y TIPO (SOLO AL SUSPENDER) */}
                        {isSuspending && (
                            <>
                                <div className="flex flex-col gap-1.5 mb-3">
                                    {suspensionConfig.config.suspensionTypes.map((typeOpt: any) => (
                                        <div key={typeOpt.id} className={`flex items-center gap-2.5 p-2.5 px-3 rounded-lg border cursor-pointer transition-all ${suspendType === typeOpt.id ? "border-red-400 bg-red-50" : "border-gray-200 bg-white hover:border-red-300"}`} onClick={() => setSuspendType(typeOpt.id)}>
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${suspendType === typeOpt.id ? "border-red-500" : "border-gray-300"}`}>
                                                {suspendType === typeOpt.id && <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>}
                                            </div>
                                            <div>
                                                <p className="text-[13px] font-semibold text-gray-900 m-0 leading-tight">{typeOpt.label}</p>
                                                <span className="text-[11px] text-gray-500">{typeOpt.description}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {showDurationSelector && (
                                    <div className="flex gap-1.5 mb-3">
                                        {suspensionConfig.config.durationOptions.map((durOpt: any) => (
                                            <button
                                                key={durOpt.value}
                                                className={`flex-1 py-1.5 text-center rounded-md border text-[11px] font-semibold cursor-pointer transition-all ${suspendDuration === durOpt.value ? "border-amber-400 text-amber-600 bg-amber-50" : "border-gray-200 text-gray-600 bg-white hover:border-gray-300 hover:bg-gray-50"}`}
                                                onClick={() => setSuspendDuration(durOpt.value)}
                                            >
                                                {durOpt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div className="h-px bg-gray-200 my-3.5"></div>
                                <textarea rows={3} placeholder="Razón de la suspensión (requerido)..." value={suspendReason} onChange={(e) => { setSuspendReason(e.target.value); if (e.target.value.trim()) setReasonError(false); }} className={`w-full bg-white border rounded-lg p-2.5 px-3 text-gray-900 text-xs resize-none outline-none transition-colors placeholder:text-gray-400 ${reasonError ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 focus:border-red-400 focus:ring-1 focus:ring-red-400"}`}></textarea>
                                {reasonError && <span className="text-[10px] text-red-500 mt-1 block">Debes ingresar una razón para suspender la cuenta.</span>}
                            </>
                        )}
                    </>
                )}

                <div className="flex gap-2 mt-4">
                    <button className="flex-1 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors shadow-sm"
                        onClick={onClose}
                        disabled={isUpdating}>
                        Cancelar
                    </button>
                    <button className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all shadow-sm ${actionType === "superhost" && selectedUser.isSuperHost ? "bg-white border border-red-200 text-red-600 hover:bg-red-50" : "border-none text-white hover:opacity-90"}`}
                        style={{ background: actionType === "superhost" ? (selectedUser.isSuperHost ? "transparent" : "linear-gradient(135deg,#f59e0b,#d97706)") : isSuspending ? "#ef4444" : "#10b981", opacity: isUpdating ? 0.7 : 1, cursor: isUpdating ? "not-allowed" : "pointer" }}
                        onClick={onConfirm}
                        disabled={isUpdating}>
                        {isUpdating ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="30" strokeLinecap="round" /></svg>Procesando...</> : actionType === "superhost" ? (selectedUser.isSuperHost ? "✕ Quitar distinción" : "⭐ Confirmar") : isSuspending ? "Confirmar suspensión" : "Reactivar cuenta"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserActionModal;